import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { ITire, defaultValue } from 'app/shared/model/tire.model';

const initialState: EntityState<ITire> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/tires';

interface IPagedTireQueryParams extends IQueryParams {
  brandOrModelSearch?: string;
  codeSearch?: string;
  companySearch?: string;
}

// Actions

export const getEntities = createAsyncThunk('tire/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<ITire[]>(requestUrl);
});

export const getTiresByCodeAndBrandOrModel = createAsyncThunk(
  'tire/fetch_entity_list',
  async ({ page, size, sort, brandOrModelSearch, codeSearch }: IPagedTireQueryParams) => {
    const requestUrl = `${apiUrl}${
      sort ? `?page=${page}&size=${size}&sort=${sort}&codeSearch=${codeSearch}&brandOrModelSearch=${brandOrModelSearch}&` : '?'
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<ITire[]>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  'tire/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ITire>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getTireCpkCount = createAsyncThunk(
  'tire/fetch_tire_cpk_count',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}/tire-cpk-count`;
    const result = await axios.get<number>(requestUrl);
    return result.data;
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'tire/create_entity',
  async (entity: ITire) => {
    const result = await axios.post<ITire>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'tire/update_entity',
  async (entity: ITire) => {
    const result = await axios.put<ITire>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'tire/partial_update_entity',
  async (entity: ITire) => {
    const result = await axios.patch<ITire>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'tire/delete_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ITire>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const TireSlice = createEntitySlice({
  name: 'tire',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities, getTiresByCodeAndBrandOrModel), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getTiresByCodeAndBrandOrModel), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = TireSlice.actions;

// Reducer
export default TireSlice.reducer;
